import { TestId } from '@modules/utilities/constants/testConstants';
import { useTranslation } from '@rbx/intl';
import React, { FC, useMemo } from 'react';
import { Typography } from '@rbx/ui';
import {
  APIReferenceMember,
  MemberType,
  MemberTypeListNameTranslKey,
  ReferenceType,
} from '@modules/client';
import { summaryID } from '../../constants/apiReferenceConstants';
import { EnumReference, ParentClassReference, Reference } from '../../types';
import ReferenceSummaryMemberTable from './ReferenceSummaryMemberTable';
import {
  getReferenceSummaryMemberTableInheritedMembers,
  getReferenceSummaryMemberTableMembers,
} from '../../utils/apiMemberUtils';
import { orderedMemberTypes } from '../../utils/apiReferenceUtils';

interface ReferenceSummaryProps {
  referenceType: ReferenceType;
  reference: Exclude<Reference, EnumReference>;
  orderedParentReferences?: ParentClassReference[] | null; // TODO
}

const getInheritedMembersIsEmpty = (
  inheritedMembers?: ReturnType<typeof getReferenceSummaryMemberTableInheritedMembers>,
) => {
  let inheritedMembersIsEmpty = false;
  if (!inheritedMembers) return true;
  if (
    !Array.isArray(inheritedMembers) &&
    inheritedMembers &&
    Object.keys(inheritedMembers).length
  ) {
    inheritedMembersIsEmpty = Object.keys(inheritedMembers).every((key) => {
      return inheritedMembers[key]?.length === 0;
    });
  } else if (Array.isArray(inheritedMembers)) {
    inheritedMembersIsEmpty = inheritedMembers.length === 0;
  }
  return inheritedMembersIsEmpty;
};

/**
 *
 * @returns Summary section at the top of class, global, data type, and library
 * engine reference pages
 */

const ReferenceSummary: FC<React.PropsWithChildren<ReferenceSummaryProps>> = ({
  referenceType,
  reference,
  orderedParentReferences,
}) => {
  // TODO: do data type references have potential parents?
  const { translate } = useTranslation();
  // data for each table under Summary
  const tables = useMemo(() => {
    return orderedMemberTypes(referenceType)
      .map((memberType) => ({
        memberType,
        title: translate(MemberTypeListNameTranslKey[memberType]) || memberType,
        members: getReferenceSummaryMemberTableMembers(reference, memberType) || [],
        inheritedMembers: orderedParentReferences
          ? getReferenceSummaryMemberTableInheritedMembers(orderedParentReferences, memberType)
          : undefined,
        isEventMemberType: memberType === MemberType.Event,
      }))
      .filter(({ members, inheritedMembers }) => {
        return !(members.length === 0 && getInheritedMembersIsEmpty(inheritedMembers));
      });
  }, [referenceType, reference, orderedParentReferences, translate]);

  return (
    <div data-testid={TestId.ApiReferenceSummary}>
      <Typography variant='h2' component='h2' id={summaryID}>
        {translate('Label.Summary') || 'Summary'}
      </Typography>
      {tables.map(({ memberType, title, members, inheritedMembers, isEventMemberType }) => {
        return members.length > 0 ||
          (inheritedMembers && Object.keys(inheritedMembers).length > 0) ? (
          <ReferenceSummaryMemberTable
            key={title}
            memberType={memberType}
            title={title}
            ariaLabel={title}
            referenceName={reference.name}
            referenceType={referenceType}
            inheritedMembers={inheritedMembers as Record<string, APIReferenceMember[]>}
            members={members as APIReferenceMember[]}
            // TODO (@ahua): fix member type declarations to use APIReferenceMember
            // instead of casting type here
            isEventMemberType={isEventMemberType}
          />
        ) : null;
      })}
    </div>
  );
};

export default ReferenceSummary;
